import {Dispatch, MutableRefObject, useState, useCallback} from 'react';

import styled from 'styled-components';
import {useDispatch} from 'react-redux';

import apiService from '~/shared/services/apiService';
import {trackEvent} from '~/shared/services/analytics';
import ManagerProvider from '~/shared/managers/ManagerProvider';
import {BodyWrapper, LoginBody} from '~/shared/components/loginStyled';
import {splitOnFirstSpace} from '~/shared/utils/general';
import {FormNamesEnum, LoginRegistrationProviders} from '~/shared/services/analytics/analyticsModels';
import {User} from '~/shared/store/models';
import {isApiError} from '~/shared/services/apiErrorService';
import {createLogger} from '~/shared/logging';
import {FormValues, FormValuesWithCodeAndToken, isFormValuesWithCodeAndToken} from '~/shared/components/LoginOrRegister/types';
import {BrandingPageTypes} from '~/shared/services/bffService/api/interfaces/payloads';
import {IFetchBrandingPageDataResponse} from '~/shared/services/bffService/api/interfaces/responses';
import VerificationForm from '~/shared/components/VerificationForm';
import {getLocalizationService} from '~/shared/services/localisationService';
import {bodyBold, bodyRegular} from '~/shared/theme/typography';
import {getDefaultAddress} from '~/shared/services/addressHelper';
import {flipOnLTR} from '~/shared/theme/utils';
import {FlexColumn} from '~/shared/theme/FlexLayout';
import {handleSuccessAuthNavigation} from '~/shared/components/LoginOrRegister/utils/authHelpers';
import {clearUserErrors} from '~/shared/store/storeModules/user/userActions';
import {OAuthProviderCredentials} from '~/shared/store/storeModules/restrictedSharedActions';

import {AuthFlowStep, LOGIN_PROVIDER_ANALYTICS_MAP, LoginProvider, ViewModes} from '../consts';
import OAuthSection from '../OAuthSection';

import SignUpForm from './SignUpForm';
import useUpdatedFormValues from './useUpdatedFormValues';

const logger = createLogger('SignUp');

const HeaderTextsWrapper = styled(FlexColumn)`
  gap: 8px;
`;

const BrandingPageTitle = styled.h1`
  ${bodyBold}
  text-align: center;
`;

const BrandingPageSubTitle = styled.h2`
  ${bodyRegular}
  text-align: center;
`;

const PhoneNumberSpan = styled.span`
  white-space: nowrap;
  ${flipOnLTR`
    margin-right: 3px;
  `}
`;

const getPhoneNumberWithDash = (phone: string) => `${phone.slice(0, 2)}X-XXXX${phone.slice(7)}`;

type SignUpProps = {
  onSubmitWithGoogleConnect?: () => void;
  backgroundColor?: string;
  onBackButtonClick?: () => void;
  pageView?: boolean;
  openLoginScreen?: (args: {email?: string; getAuthCodeForProviderConnect?: boolean}) => void;
  loginProvider: LoginProvider;
  onSuccess?: (user?: User) => void;
  withTransparentBG?: boolean;
  disableExternalProvidersRegistration?: boolean;
  companyToken?: string;
  className?: string;
  emailValidationErrorKey?: string;
  openProviderScreen?: (options: {
    providerProps: Record<string, string | number | undefined>;
    nextLoginProvider: LoginProvider;
    nextScreen: ViewModes;
  }) => void;
  brandingPageData?: Record<
    BrandingPageTypes.REGISTRATION | BrandingPageTypes.OTP,
    IFetchBrandingPageDataResponse | undefined
  >;
  isRedirectedToStepRef?: MutableRefObject<boolean>;
  fromCheckout?: boolean;
  authFlowStep?: AuthFlowStep;
  setAuthFlowStep?: Dispatch<AuthFlowStep>;
  closeModal?: () => void;
  joinPage?: boolean;
  email?: string;
  fullName?: string;
} & OAuthProviderCredentials;

const SignUp = ({
  backgroundColor: _backgroundColor,
  pageView,
  openLoginScreen,
  loginProvider,
  onSuccess,
  withTransparentBG,
  disableExternalProvidersRegistration = false,
  companyToken,
  className,
  brandingPageData,
  emailValidationErrorKey,
  openProviderScreen,
  onSubmitWithGoogleConnect,
  isRedirectedToStepRef,
  fromCheckout,
  authFlowStep,
  setAuthFlowStep,
  closeModal,
  joinPage,
  email,
  fullName,
  googleCode,
}: SignUpProps) => {
  const {t} = getLocalizationService();
  const dispatch = useDispatch();
  const [activationModeProps, setActivationModeProps] = useState<{signUpCredentials: FormValues} | null>(null);
  const [sendActivationCodeError, setSendActivationCodeError] = useState('');
  const [activationToken, setActivationToken] = useState<string>('');
  const [activationCodeError, setActivationCodeError] = useState<string | null>(null);
  const [shouldFocusPhone, setShouldFocusPhone] = useState(false);

  const {registerFormValues, setRegisterFormValues} = useUpdatedFormValues({
    email,
    fullName,
    googleCode,
  });

  const onSendActivationCode = async (signUpCredentials: FormValues) => {
    try {
      const {data: activationCodeData} = await apiService.getActivationTokenAndSendActivationCodeToUser({
        cellPhone: signUpCredentials.cellPhone,
        email: signUpCredentials.email,
        token: companyToken,
      });
      if (activationCodeData) {
        setActivationModeProps({signUpCredentials});
        setActivationToken(activationCodeData);
        setSendActivationCodeError('');
        if (setAuthFlowStep) {
          setAuthFlowStep(AuthFlowStep.OTP);
        }
      }
    } catch (error) {
      if (isApiError(error)) {
        trackEvent('formErrorMessageView', {
          formName: FormNamesEnum.REGISTRATION,
          errorType: error?.message,
          onCheckout: fromCheckout,
          authenticationProvider: LOGIN_PROVIDER_ANALYTICS_MAP[loginProvider],
        });
        if (error?.message) {
          setSendActivationCodeError(error?.message);
        } else {
          setSendActivationCodeError(
            'the_phone_number_you_entered_already_exists_please_login_or_signup_with_different_number',
          );
        }

        return;
      }

      logger.error('Unexpected Error');
    }
  };

  const onSubmitNewUser = (signUpCredentials: FormValues) => {
    if (loginProvider === LoginProvider.TENBIS) {
      trackEvent('registrationForm_click', {onCheckout: fromCheckout, authenticationProvider: LoginRegistrationProviders.EMAIL});
    }
    trackEvent('hasSubmittedRegister', {onCheckout: fromCheckout, authenticationProvider: LOGIN_PROVIDER_ANALYTICS_MAP[loginProvider]});
    setRegisterFormValues(signUpCredentials);
    onSendActivationCode(signUpCredentials);
  };

  const onSubmitActivationCodeUser = async (signUpCredentials: Partial<FormValuesWithCodeAndToken>) => {
    if (!isFormValuesWithCodeAndToken(signUpCredentials)) {
      return;
    }
    const [firstName, lastName] = splitOnFirstSpace(signUpCredentials.fullName);
    const analyticsDataProps = {
      onCheckout: fromCheckout,
      authenticationProvider: LOGIN_PROVIDER_ANALYTICS_MAP[loginProvider],
    };

    trackEvent('registrationOTP_submit', {onCheckout: fromCheckout, authenticationProvider: LOGIN_PROVIDER_ANALYTICS_MAP[loginProvider]});
    try {
      const userData = await ManagerProvider.registerUser({
        firstName,
        lastName,
        ...signUpCredentials,
        token: companyToken,
      }, analyticsDataProps, !!companyToken);

      if (!userData) {
        return;
      }

      setActivationCodeError('');

      const defaultAddress = getDefaultAddress();
      if (pageView && !defaultAddress && setAuthFlowStep) {
        setAuthFlowStep(AuthFlowStep.ADD_ADDRESS);
        return;
      }
      if (onSuccess) {
        onSuccess(userData);
      }
      if (!joinPage) {
        handleSuccessAuthNavigation({pageView, closeModal, fromCheckout});
      }
    } catch (error) {
      trackEvent('formErrorMessageView', {
        formName: FormNamesEnum.OTP_REGISTRATION,
        errorType: !!error && typeof error === 'object' && 'message' in error && error.message,
        onCheckout: fromCheckout,
        authenticationProvider: LOGIN_PROVIDER_ANALYTICS_MAP[loginProvider],
      });
      setActivationCodeError(t('activation_code_entry_is_wrong_please_try_again'));
      throw error;
    }
  };

  const backgroundColor = withTransparentBG ? 'transparent' : _backgroundColor;

  const onSubmitCodeHandler = async ({authenticationCode}: {authenticationCode?: string | undefined}) => {
    await onSubmitActivationCodeUser({
      ...activationModeProps?.signUpCredentials,
      authenticationToken: activationToken,
      authenticationCode,
    });
  };

  const onReSendCodeHandler = () => {
    if (!activationModeProps?.signUpCredentials) {
      return;
    }
    trackEvent('hasClickedSendCodeAgain', {onCheckout: fromCheckout, authenticationProvider: LOGIN_PROVIDER_ANALYTICS_MAP[loginProvider]});
    onSendActivationCode(activationModeProps.signUpCredentials);
  };

  const onUpdatePhoneNumberClick = useCallback(() => {
    dispatch(clearUserErrors());
    if (setAuthFlowStep) {
      setAuthFlowStep(AuthFlowStep.INITIAL);
      setShouldFocusPhone(true);
    }
  }, [setAuthFlowStep, dispatch]);

  const isFixedEmail = loginProvider === LoginProvider.GOOGLE;

  const onPhoneFocused = useCallback(() => setShouldFocusPhone(false), []);

  return (
    <>
      <BodyWrapper
        id="register_tab_controls"
        aria-labelledby="register_tab_button"
        role="tabpanel"
        pageView={pageView}
        className={className}
        backgroundColor={backgroundColor}
      >
        <LoginBody className="login-body" verificationMode={authFlowStep === AuthFlowStep.OTP}>
          {authFlowStep === AuthFlowStep.OTP ? (
            <VerificationForm
              onReSendCode={onReSendCodeHandler}
              onSubmit={onSubmitCodeHandler}
              toUpdatePhoneNumberScreen={onUpdatePhoneNumberClick}
              subHeaderText={
                <HeaderTextsWrapper>
                  <BrandingPageTitle>
                    {registerFormValues.email}
                  </BrandingPageTitle>
                  <BrandingPageSubTitle>
                    {t('we_texted_a_code_to')}
                    <PhoneNumberSpan>
                      {activationModeProps && getPhoneNumberWithDash(activationModeProps.signUpCredentials?.cellPhone)}
                    </PhoneNumberSpan>
                  </BrandingPageSubTitle>
                </HeaderTextsWrapper>
              }
              resErrors={activationCodeError}
              setResErrors={setActivationCodeError}
            />
          ) : (
            <>
              <SignUpForm
                onSubmit={onSubmitNewUser}
                initialValues={registerFormValues}
                sendActivationCodeError={sendActivationCodeError}
                existingEmailAction={openLoginScreen ? emailStr => openLoginScreen({email: emailStr}) : undefined}
                borderedInputs={!!withTransparentBG}
                title={brandingPageData?.registration?.title}
                subtitle={brandingPageData?.registration?.subTitle}
                emailValidationErrorKey={emailValidationErrorKey}
                isFixedEmail={isFixedEmail}
                loginProvider={loginProvider}
                shouldFocusPhone={shouldFocusPhone}
                onPhoneFocused={onPhoneFocused}
              />
              {loginProvider === LoginProvider.TENBIS && !disableExternalProvidersRegistration && openProviderScreen && (
                <OAuthSection
                  onSubmitWithGoogleConnect={onSubmitWithGoogleConnect}
                  openProviderScreen={openProviderScreen}
                  openLoginScreen={openLoginScreen}
                  isRedirectedToStepRef={isRedirectedToStepRef}
                  isRegistration
                  fromCheckout={fromCheckout}
                />
              )}
            </>
          )}
        </LoginBody>
      </BodyWrapper>
    </>
  );
};

export default SignUp;
